import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { difference, filter, intersection, isEmpty, some, noop, once } from "lodash";
import { Button } from "components/buttons/button/Button";
import { connect, type ConnectedProps } from "react-redux";

import ModalWithSteps from "../../modal/ModalWithSteps";
import ConfirmLicensingStep from "../commonSteps/confirmLicensingStep/ConfirmLicensingStep";
import NotifyStep from "../commonSteps/notifyStep/NotifyStep";
import { GroupListStep } from "./groupAssignmentSteps";
import { CommunicationChannels, RolePermissions, type SortingDirection, type TemplateTypes } from "../../../enums";
import { Awareness, Enrollment } from "../../../enums/groups";
import { type Group, type NotifyStepSettings, bindAction } from "../../../interfaces";
import { Context } from "enums/licenseContext";
import useNotifyConfig from "../../../features/SystemNotifications/hooks/useNotifyConfig";
import { initialNotifyConfigDefault } from "../../../features/SystemNotifications/config";
import { NotifyStepSwitch } from "../../notifyStep/NotifyStepSwitch";
import useToggle from "../../../hooks/useToggle";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../featureFlags";
import { type LicenseConfirmationFormRef } from "../../../features/Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/LicenseConfirmationBlockV2/LicenseConfirmationBlock";
import PaymentModal from "../../../features/Licensing/Packs/PaymentModal/PaymentModal";
import NotificationModal, { type NotificationModalState } from "../../modal/NotificationModal/NotificationModal";
import Observable from "../../../utils/Observable";
import { updateLicensePackInfo } from "../../../features/Licensing/ContentAssignmentModalLicensingSteps/state/slices/licenseConfirmationInfoSlice";
import { SwitchEntityTypes } from "../../../enums/switchEntityTypes";
import { type AppDispatch, type RootState } from "../../../features/Application/globaltypes/redux";
import "./groupsAssignmentModal.scss";
import { type FetchGroupsPayload } from "features/People/Groups/GroupsOverview/state/groupsOverviewActions";
import SearchInput from "components/searchInput/SearchInput";

export interface UserGroupsAssignment {
  userId: number;
  groupIds: number[];
}

interface Common {
  renderTrigger?: (closeModal: () => void) => React.ReactElement;
  showModal: boolean;
  onCancel: () => void;
  onConfirm: (notifyStepSettings?: NotifyStepSettings) => void;
  onGroupsSelected: (groupIds: number[]) => Promise<boolean>;
  selectedUserIds: number[];
  isStepLoading?: boolean;
}

interface GroupsStep {
  accountId: number;
  fetchGroups: (payload: FetchGroupsPayload) => void | any;
  groups: Group[];
  groupsCount: number;
  userGroupsAssignments: UserGroupsAssignment[];
  filterOptions: any;
  fetchFilterOptions: (accountId: number) => void;
  isGroupsLoading: boolean;
  notifyStepDisabled?: boolean;
  notifyTemplateType: TemplateTypes;
  onSearchChanged: (value: string) => void;
  search: string;
  setReloadListItems: (reloadItems: (enableSorting: boolean) => void) => void;
}

const initNotifyConfig = {
  ...initialNotifyConfigDefault,
  shouldNotifyUsers: false,
};

export type GroupsAssignmentModalProps = Common & GroupsStep & PropsFromRedux;

const GroupsAssignmentModal = (props: GroupsAssignmentModalProps) => {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [issuingUsers, setIssuingUsers] = useState<number[]>([]);
  const [appliedFilter, setAppliedFilter] = useState({});
  const [isValid, setIsValid] = useState(true);
  const [isNotifyStepValid, setIsNotifyStepValid] = useState(true);
  const onPreviousNotifyStepRef = useRef<() => Promise<void>>();
  const getNotifySettingsRef = useRef<() => Promise<NotifyStepSettings | undefined>>();
  const [notifyConfig, { setNotifyConfig, resetNotifyConfig, shouldNotify, communicationChannel }] =
    useNotifyConfig(initNotifyConfig);

  const [skipLicensingStep, setSkipLicensingStep] = useState(false);
  const [showPaymentModal, togglePaymentModal] = useToggle(false);
  const additionalPurchaseEnabled = useFeatureFlag(FeatureFlags.AdditionalPurchase);
  const licenseConfirmationFormRef = useRef<LicenseConfirmationFormRef>({ getValues: () => [] });
  const onTriggerNotificationModalObserver = useMemo(() => new Observable(), []);

  const { search, onSearchChanged } = props;

  useEffect(() => {
    setSelectedGroupIds([]);
    setIssuingUsers([]);
    setAppliedFilter({});
  }, [props.showModal]);
  const [allGroupsAware, setGroupsAware] = useState(true);

  useEffect(() => {
    const selectedGroups = props.groups.filter((g) => selectedGroupIds.includes(g.id));
    const newValue = selectedGroups.every((g) => g.isAware === Awareness.Aware);
    setGroupsAware(newValue);
  }, [selectedGroupIds, props.groups, setGroupsAware]);

  useEffect(() => {
    !props.showModal && resetNotifyConfig();
  }, [props.showModal, resetNotifyConfig]);

  const onSelectedGroupsChanged = (groupIds: number[]) => {
    const filteredIssuingUsers = filter(props.selectedUserIds, (userId) =>
      some(filter(props.userGroupsAssignments, { userId }), (x: UserGroupsAssignment) =>
        some(difference(groupIds, x.groupIds)),
      ),
    );
    setSelectedGroupIds(groupIds);
    setIssuingUsers(filteredIssuingUsers);
  };

  const renderGroupListStepActions = (nextStep: Function) => (closeModal: Function) => (
    <>
      <Button
        basic
        color="blue"
        className="cancel"
        content="Cancel"
        onClick={() => {
          closeModal();
          props.onCancel();
        }}
      />
      <Button
        blur
        primary
        className="next"
        content="Add"
        onClick={async () => {
          if (issuingUsers.length) {
            const shouldShowModal = await props.onGroupsSelected(selectedGroupIds);
            setSkipLicensingStep(!shouldShowModal);
            nextStep();
          } else {
            props.onConfirm();
            closeModal();
          }
        }}
        disabled={isEmpty(selectedGroupIds) || props.isStepLoading}
      />
    </>
  );

  const renderConfirmLicensingStepActions = (nextStep: Function, prevStep: Function) => (closeModal: Function) => {
    let actionText: string = additionalPurchaseEnabled && props.isPurchaseNeeded ? "Proceed to Payment" : "Next";

    return (
      <>
        <Button blur primary content="Previous" className="previous" onClick={() => prevStep()} />
        <Button
          blur
          primary
          className="next"
          content={actionText}
          disabled={!isValid}
          onClick={once(async () => {
            if (additionalPurchaseEnabled && props.isPurchaseNeeded) {
              props.updateLicensePackInfoAction?.(licenseConfirmationFormRef.current.getValues());
              togglePaymentModal();
              closeModal();
            } else {
              setIsValid(false);
              setIsDataLoaded(false);
              nextStep();
            }
          })}
        />
      </>
    );
  };

  const renderNotifyStepActions = (_: Function, prevStep: Function) => (closeModal: Function) => (
    <>
      <Button
        blur
        primary
        content="Previous"
        className="previous"
        onClick={() => {
          onPreviousNotifyStepRef?.current?.();
          prevStep();
        }}
      />
      <Button
        primary
        className="confirm"
        content="Finish"
        disabled={
          shouldNotify &&
          ((communicationChannel === CommunicationChannels.Email && !isDataLoaded) || !isNotifyStepValid)
        }
        onClick={async () => {
          const notifySettingsRef = await getNotifySettingsRef?.current?.();
          props.onConfirm(notifySettingsRef);
          closeModal();
        }}
      />
    </>
  );

  const fetchGroups = (
    skip: number,
    top: number,
    sortingColumnName: string,
    sortingDirection: SortingDirection,
    groupsFilter: any,
  ) => {
    const filterParams = {
      ...groupsFilter,
      IsWithAutoEnroll: Enrollment.Manual,
    };

    props.fetchGroups({
      skip,
      top,
      sortBy: sortingColumnName,
      sortOrder: sortingDirection,
      term: search,
      ...filterParams,
    });
  };

  const renderSearch = useCallback(() => {
    return <SearchInput placeholder="Search for groups..." defaultValue={search} onChange={onSearchChanged} />;
  }, [onSearchChanged, search]);

  const onPaymentSuccess = () => {
    togglePaymentModal();
    const entityState: NotificationModalState = {
      entityType: SwitchEntityTypes.Flow,
      templateType: props.notifyTemplateType,
    };
    onTriggerNotificationModalObserver.notify(noop, entityState);
  };

  const onClosePaymentModal = () => {
    togglePaymentModal();
    props.onCancel();
  };

  return (
    <>
      <ModalWithSteps
        className={"groups-assignment-modal"}
        scrolling
        renderTrigger={props.renderTrigger}
        showModal={props.showModal}
        onCancel={props.onCancel}
        isLoading={props.isStepLoading}
      >
        <GroupListStep
          accountId={props.accountId}
          header="Add to Group"
          renderModalActions={renderGroupListStepActions}
          loadPage={fetchGroups}
          isLoading={props.isGroupsLoading}
          groups={props.groups}
          groupsAmount={props.groupsCount}
          userGroupsAssignments={props.userGroupsAssignments}
          onSelectedGroupsChanged={onSelectedGroupsChanged}
          selectedGroupIds={selectedGroupIds}
          selectedUserIds={props.selectedUserIds}
          filterOptions={props.filterOptions}
          fetchFilterOptions={props.fetchFilterOptions}
          filter={appliedFilter}
          applyFilter={setAppliedFilter}
          resetFilter={() => setAppliedFilter({})}
          hasPermissionPredicate={(userPermissions) =>
            intersection(userPermissions, [RolePermissions.GroupsView]).length > 0
          }
          renderSearch={renderSearch}
          setReloadListItems={props.setReloadListItems}
        />
        {!skipLicensingStep && (
          <ConfirmLicensingStep
            header="License Confirmation"
            renderModalActions={renderConfirmLicensingStepActions}
            onIsDataValidChange={setIsValid}
            info={{
              groupIds: selectedGroupIds,
              userIds: props.selectedUserIds,
            }}
            context={Context.AddPeopleToGroups}
            licenseConfirmationFormRef={licenseConfirmationFormRef}
          />
        )}
        <NotifyStep
          preRender
          header="Notify"
          renderModalActions={renderNotifyStepActions}
          onIsDataValidChange={setIsNotifyStepValid}
          onIsDataLoaded={setIsDataLoaded}
          isDataLoaded={isDataLoaded}
          onPreviousNotifyStepRef={onPreviousNotifyStepRef}
          getNotifySettingsRef={getNotifySettingsRef}
          notifyStepDisabled={props.notifyStepDisabled || !allGroupsAware}
          notifyTemplateType={props.notifyTemplateType}
          renderSwitch={(switchProps) => (
            <NotifyStepSwitch config={notifyConfig} onNotifyConfigChange={setNotifyConfig} switchProps={switchProps} />
          )}
          shouldNotify={shouldNotify}
          communicationChannel={communicationChannel}
        />
      </ModalWithSteps>
      {showPaymentModal && props.paymentPacks && (
        <PaymentModal
          paymentPacks={props.paymentPacks}
          onClose={onClosePaymentModal}
          onPaymentSuccess={onPaymentSuccess}
        />
      )}
      {additionalPurchaseEnabled && (
        <NotificationModal
          onTriggerPublishContentObserver={onTriggerNotificationModalObserver}
          onConfirmAction={(notificationConfirmPayload) =>
            props.onConfirm(notificationConfirmPayload.notificationSettings)
          }
          onClose={props.onCancel}
        />
      )}
    </>
  );
};

export { GroupsAssignmentModal as GroupsAssignmentModalUnconnected };

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => ({
  isPurchaseNeeded: state.licensing?.availableLicenses?.isPurchaseNeeded,
  paymentPacks: state.licensing.licenseConfirmationInfo.paymentPacks,
});

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateLicensePackInfoAction: bindAction(updateLicensePackInfo, dispatch),
});

/* istanbul ignore next */
const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(GroupsAssignmentModal);
export default ConnectedComponent;
